import { card, emptyNote, sectionTitle } from "../components/ui.js";
import { DONEM } from "../config.js";
import { B, staffOf } from "../domain/context.js";
import { staffAvatar } from "../lib/avatar.js";
import { donemAdi, esc, fmtDate, money } from "../lib/fmt.js";
import {
  acikAvanslar,
  avansBakiyesi,
  bekleyenAvanslar,
  bordroKilitli,
  bordroUret,
  personelAvanslari,
  taksitTutari,
} from "../domain/payroll.js";
import {
  GIDER_KATEGORI,
  butceKarsilastirma,
  donemGiderleri,
  donemSonucu,
  kasaDefteri,
  onayBekleyenler,
  subeKarsilastirma,
} from "../domain/expenses.js";
import { state } from "../state.js";

const SEKMELER = [
  { id: "ozet", label: "Özet" },
  { id: "bordro", label: "Bordro" },
  { id: "avans", label: "Avanslar" },
  { id: "gider", label: "Giderler" },
  { id: "kasa", label: "Kasa" },
];

export function viewFinans() {
  const aktif = state.finansTab ?? "ozet";
  const govde = {
    ozet: ozetSekmesi,
    bordro: bordroSekmesi,
    avans: avansSekmesi,
    gider: giderSekmesi,
    kasa: kasaSekmesi,
  }[aktif] ?? ozetSekmesi;

  return `
  <div class="flex items-center gap-1 rounded-xl bg-line/50 p-1 mb-4 w-fit overflow-x-auto">
    ${SEKMELER.map(s => `
      <button data-finanstab="${s.id}"
        class="rounded-lg px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition ${aktif === s.id ? "bg-white shadow-card" : "text-inksoft hover:text-ink"}">
        ${s.label}
      </button>`).join("")}
  </div>
  ${govde()}`;
}

// ---------- Özet ----------
function ozetSekmesi() {
  const t = B();
  const s = donemSonucu(t.id);
  const karsilastirma = subeKarsilastirma();
  const butce = butceKarsilastirma(t.id);

  return `
  <div class="grid gap-3 sm:gap-4 grid-cols-2 xl:grid-cols-4 mb-4">
    ${[
      { label: "Gelir (tahakkuk)", value: money(s.gelir.tahakkuk), sub: `${money(s.gelir.tahsil)} tahsil edildi`, color: "teal" },
      { label: "Gider", value: money(s.gider), sub: `${money(s.kategoriler.personel)} personel`, color: "coral" },
      { label: "Net Sonuç", value: money(s.net), sub: s.marj == null ? "—" : `%${s.marj} marj`, color: s.net >= 0 ? "teal" : "coral" },
      { label: "Öğrenci Başına", value: money(s.ogrenciBasinaMaliyet), sub: `maliyet · gelir ${money(s.ogrenciBasinaGelir)}`, color: "grape" },
    ].map(x => card(`
      <div class="p-4">
        <div class="text-xs font-semibold text-inksoft">${x.label}</div>
        <div class="font-display font-extrabold text-2xl mt-1 text-${x.color} break-words">${x.value}</div>
        <div class="text-[11px] text-inksoft/80 mt-0.5">${x.sub}</div>
      </div>`, "min-w-0")).join("")}
  </div>

  <div class="grid gap-4 lg:grid-cols-2 items-start">
    ${card(`
      <div class="p-5">
        ${sectionTitle("Gider Dağılımı", `<span class="text-[11px] text-inksoft">${donemAdi(DONEM)}</span>`)}
        ${Object.entries(s.kategoriler)
          .filter(([, v]) => v > 0)
          .sort((a, b) => b[1] - a[1])
          .map(([kod, tutar]) => {
            const oran = s.gider ? Math.round((tutar / s.gider) * 100) : 0;
            return `
            <div class="mb-2.5">
              <div class="flex items-center justify-between gap-2 text-sm">
                <span class="truncate">${esc(GIDER_KATEGORI[kod]?.label ?? kod)}</span>
                <span class="shrink-0 font-semibold">${money(tutar)} <span class="text-[11px] text-inksoft">%${oran}</span></span>
              </div>
              <div class="h-1.5 rounded-full bg-line mt-1 overflow-hidden">
                <div class="h-full rounded-full bg-coral" style="width:${oran}%"></div>
              </div>
            </div>`;
          }).join("")}
      </div>`, "min-w-0")}

    ${card(`
      <div class="p-5">
        ${sectionTitle("Şube Karşılaştırma", `<span class="text-[11px] text-inksoft hidden sm:block">Net sonuca göre</span>`)}
        <div class="overflow-x-auto">
          <table class="w-full text-sm min-w-[380px]">
            <thead><tr class="text-left text-[11px] uppercase tracking-wide text-inksoft border-b border-line">
              <th class="py-2 pr-3">Şube</th><th class="py-2 pr-3 text-right">Gelir</th>
              <th class="py-2 pr-3 text-right">Gider</th><th class="py-2 text-right">Net</th></tr></thead>
            <tbody class="divide-y divide-line/70">
              ${karsilastirma.map(x => `
                <tr class="${x.branchId === t.id ? "bg-paper/60" : ""}">
                  <td class="py-2.5 pr-3 font-semibold truncate">${esc(x.branchName)}</td>
                  <td class="py-2.5 pr-3 text-right whitespace-nowrap">${money(x.gelir.tahakkuk)}</td>
                  <td class="py-2.5 pr-3 text-right whitespace-nowrap text-inksoft">${money(x.gider)}</td>
                  <td class="py-2.5 text-right whitespace-nowrap font-display font-bold ${x.net >= 0 ? "text-teal" : "text-coral"}">${money(x.net)}</td>
                </tr>`).join("")}
            </tbody>
          </table>
        </div>
      </div>`, "min-w-0")}

    ${butce ? card(`
      <div class="p-5">
        ${sectionTitle("Bütçe / Gerçekleşen")}
        <div class="overflow-x-auto">
          <table class="w-full text-sm min-w-[420px]">
            <thead><tr class="text-left text-[11px] uppercase tracking-wide text-inksoft border-b border-line">
              <th class="py-2 pr-3">Kategori</th><th class="py-2 pr-3 text-right">Bütçe</th>
              <th class="py-2 pr-3 text-right">Gerçekleşen</th><th class="py-2 text-right">Sapma</th></tr></thead>
            <tbody class="divide-y divide-line/70">
              ${butce.filter(x => x.hedef || x.olan).map(x => `
                <tr>
                  <td class="py-2.5 pr-3 truncate">${esc(x.label)}</td>
                  <td class="py-2.5 pr-3 text-right text-inksoft whitespace-nowrap">${money(x.hedef)}</td>
                  <td class="py-2.5 pr-3 text-right whitespace-nowrap">${money(x.olan)}</td>
                  <td class="py-2.5 text-right whitespace-nowrap font-semibold ${x.asim ? "text-coral" : "text-teal"}">
                    ${x.sapma >= 0 ? "+" : ""}${money(x.sapma)}
                  </td>
                </tr>`).join("")}
            </tbody>
          </table>
        </div>
      </div>`, "min-w-0 lg:col-span-2") : ""}
  </div>`;
}

// ---------- Bordro ----------
function bordroSekmesi() {
  const t = B();
  const b = bordroUret(t.id);
  const kilitli = bordroKilitli(t.id);

  return `
  <div class="grid gap-3 sm:gap-4 grid-cols-2 xl:grid-cols-4 mb-4">
    ${[
      { label: "Brüt Toplam", value: money(b.totalGross), sub: `${b.items.length} personel`, color: "sky" },
      { label: "Net Ödenecek", value: money(b.totalNet), sub: `ayın ${t.staff[0]?.compensation?.payDay ?? 5}'inde`, color: "teal" },
      { label: "Kesintiler", value: money(b.totalDeductions), sub: "sigorta, sandık, vergi, avans", color: "sun" },
      { label: "İşveren Maliyeti", value: money(b.totalEmployerCost), sub: "işveren payları dahil", color: "coral" },
    ].map(x => card(`
      <div class="p-4">
        <div class="text-xs font-semibold text-inksoft">${x.label}</div>
        <div class="font-display font-extrabold text-2xl mt-1 text-${x.color} break-words">${x.value}</div>
        <div class="text-[11px] text-inksoft/80 mt-0.5">${x.sub}</div>
      </div>`, "min-w-0")).join("")}
  </div>

  ${card(`
    <div class="p-5">
      ${sectionTitle(
        `${donemAdi(DONEM)} Bordrosu`,
        kilitli
          ? `<span class="text-[11px] font-bold rounded-full bg-teal-soft text-teal px-2.5 py-1">Onaylandı — kilitli</span>`
          : `<span class="text-[11px] font-bold rounded-full bg-sun-soft text-[#B07E14] px-2.5 py-1">Taslak</span>`,
      )}
      <p class="text-[11px] text-inksoft mb-3">
        Tutarlar dönemin maaş sözleşmesinden hesaplanır, kayıtta saklanmaz —
        zam geçmişi ayrı tutulduğu için geçmiş bordro her zaman yeniden üretilebilir.
        Onaylandıktan sonra kilitlenir; düzeltme ters kayıtla yapılır.
      </p>

      <div class="overflow-x-auto">
        <table class="w-full text-sm min-w-[680px]">
          <thead><tr class="text-left text-[11px] uppercase tracking-wide text-inksoft border-b border-line">
            <th class="py-2.5 pr-3">Personel</th>
            <th class="py-2.5 pr-3 text-right">Brüt</th>
            <th class="py-2.5 pr-3 text-right">Ek</th>
            <th class="py-2.5 pr-3 text-right">Kesinti</th>
            <th class="py-2.5 pr-3 text-right">Net</th>
            <th class="py-2.5 text-right">İşveren</th>
          </tr></thead>
          <tbody class="divide-y divide-line/70">
            ${b.items.map(x => `
              <tr class="cursor-pointer hover:bg-paper/60" data-staff="${x.staffId}">
                <td class="py-2.5 pr-3">
                  <span class="flex items-center gap-2 min-w-0">
                    ${staffAvatar(staffOf(x.staffId), "w-7 h-7")}
                    <span class="min-w-0">
                      <span class="block font-semibold truncate">${esc(x.name)}</span>
                      <span class="block text-[11px] text-inksoft truncate">${esc(x.role)}</span>
                    </span>
                  </span>
                </td>
                <td class="py-2.5 pr-3 text-right whitespace-nowrap">${money(x.gross)}</td>
                <td class="py-2.5 pr-3 text-right whitespace-nowrap text-teal">${x.toplamEk ? "+" + money(x.toplamEk) : "—"}</td>
                <td class="py-2.5 pr-3 text-right whitespace-nowrap text-coral">
                  −${money(x.toplamKesinti)}
                  ${x.deductions.some(d => d.label === "Avans mahsubu")
                    ? `<span class="block text-[10px] text-inksoft">avans dahil</span>` : ""}
                </td>
                <td class="py-2.5 pr-3 text-right whitespace-nowrap font-display font-bold">${money(x.net)}</td>
                <td class="py-2.5 text-right whitespace-nowrap text-inksoft">${money(x.isverenMaliyeti)}</td>
              </tr>`).join("")}
          </tbody>
        </table>
      </div>

      ${b.items.some(x => x.personelCocugu) ? `
        <div class="rounded-xl bg-grape-soft p-3 mt-4 text-xs">
          <span class="font-bold text-grape">Personel çocuğu indirimi:</span>
          ${b.items.filter(x => x.personelCocugu).map(x =>
            `${esc(x.name)} → ${esc(x.personelCocugu.name)} (%${x.personelCocugu.pct})`).join(" · ")}
          <div class="text-inksoft mt-1">
            İndirimin kuruma maliyeti personel maliyetinin parçasıdır; ücret tarifesinde ayrıca görünür.
          </div>
        </div>` : ""}
    </div>`)}`;
}

// ---------- Avanslar ----------
function avansSekmesi() {
  const t = B();
  const acik = acikAvanslar(t.id);
  const bekleyen = bekleyenAvanslar(t.id);
  const toplamAcik = acik.reduce((a, x) => a + x.amount, 0);

  const satir = a => {
    const kisi = staffOf(a.staffId);
    return `
    <li class="rounded-xl border ${a.approvedBy ? "border-line" : "border-sun"} p-3">
      <div class="flex items-center gap-2">
        ${staffAvatar(kisi, "w-8 h-8")}
        <span class="min-w-0 flex-1">
          <span class="block text-sm font-semibold truncate">${esc(kisi?.name ?? "—")}</span>
          <span class="block text-[11px] text-inksoft truncate">${esc(a.reason)}</span>
        </span>
        <span class="text-right shrink-0">
          <span class="block font-display font-bold">${money(a.amount)}</span>
          ${a.installments > 1 ? `<span class="block text-[10px] text-inksoft">${a.installments} taksit · ${money(taksitTutari(a))}/ay</span>` : ""}
        </span>
      </div>
      <div class="flex items-center justify-between gap-2 text-[11px] text-inksoft mt-2 pt-2 border-t border-line/70">
        <span>
          ${a.paidAt ? `${fmtDate(a.paidAt)} ödendi · ${esc(a.method ?? "—")}` : "Ödeme bekliyor"}
          · ${donemAdi(a.deductPeriod)} bordrosundan kesilecek
        </span>
        ${a.approvedBy
          ? `<span class="shrink-0 text-teal font-semibold">✓ ${esc(a.approvedBy)}</span>`
          : `<span class="shrink-0 text-[#B07E14] font-bold">Onay bekliyor</span>`}
      </div>
    </li>`;
  };

  return `
  <div class="grid gap-3 sm:gap-4 grid-cols-2 xl:grid-cols-4 mb-4">
    ${[
      { label: "Açık Avans", value: money(toplamAcik), sub: `${acik.length} kayıt`, color: "coral" },
      { label: "Onay Bekleyen", value: bekleyen.length, sub: "talep", color: "sun" },
      { label: "Bu Dönem Kesilecek", value: money(acik.filter(a => a.deductPeriod <= DONEM).reduce((s, a) => s + taksitTutari(a), 0)), sub: donemAdi(DONEM), color: "sky" },
      { label: "Personel", value: new Set(acik.map(a => a.staffId)).size, sub: "avansı olan", color: "grape" },
    ].map(x => card(`
      <div class="p-4">
        <div class="text-xs font-semibold text-inksoft">${x.label}</div>
        <div class="font-display font-extrabold text-2xl mt-1 text-${x.color} break-words">${x.value}</div>
        <div class="text-[11px] text-inksoft/80 mt-0.5">${x.sub}</div>
      </div>`, "min-w-0")).join("")}
  </div>

  ${card(`
    <div class="p-5">
      ${sectionTitle("Avans Kayıtları")}
      <p class="text-[11px] text-inksoft mb-3">
        Avans talebi → yönetici onayı → ödeme → bordroda otomatik kesinti.
        Taksitli avansta her dönem yalnızca taksit tutarı düşer.
      </p>
      ${avanslarListesi(t)}
    </div>`)}

  ${bekleyen.length ? card(`
    <div class="p-5">
      ${sectionTitle("Onay Bekleyen Talepler", `<span class="text-[11px] font-bold rounded-full bg-sun-soft text-[#B07E14] px-2.5 py-1">${bekleyen.length}</span>`)}
      <ul class="space-y-2">${bekleyen.map(satir).join("")}</ul>
    </div>`, "mt-4") : ""}`;

  function avanslarListesi(t) {
    const list = acikAvanslar(t.id);
    if (!list.length) return emptyNote("Açık avans kaydı yok.");
    return `<ul class="space-y-2">${list.map(satir).join("")}</ul>`;
  }
}

// ---------- Giderler ----------
function giderSekmesi() {
  const t = B();
  const list = donemGiderleri(t.id).sort((a, b) => b.date.localeCompare(a.date));
  const bekleyen = onayBekleyenler(t.id);

  return `
  ${bekleyen.length ? `
    <div class="rounded-xl bg-sun-soft text-[#B07E14] text-sm font-semibold px-4 py-3 mb-4">
      ${bekleyen.length} gider onay veya ödeme bekliyor.
    </div>` : ""}

  ${card(`
    <div class="p-5">
      ${sectionTitle(`${donemAdi(DONEM)} Giderleri`, `<span class="text-[11px] text-inksoft">${list.length} kayıt · ${money(list.reduce((a, g) => a + g.amount, 0))}</span>`)}
      ${list.length ? `
        <div class="overflow-x-auto">
          <table class="w-full text-sm min-w-[640px]">
            <thead><tr class="text-left text-[11px] uppercase tracking-wide text-inksoft border-b border-line">
              <th class="py-2.5 pr-3">Tarih</th><th class="py-2.5 pr-3">Kategori</th>
              <th class="py-2.5 pr-3">Açıklama</th><th class="py-2.5 pr-3">Tedarikçi</th>
              <th class="py-2.5 pr-3 text-right">Tutar</th><th class="py-2.5">Durum</th>
            </tr></thead>
            <tbody class="divide-y divide-line/70">
              ${list.map(g => `
                <tr>
                  <td class="py-2.5 pr-3 whitespace-nowrap text-inksoft">${fmtDate(g.date)}</td>
                  <td class="py-2.5 pr-3 whitespace-nowrap">
                    <span class="text-[11px] font-semibold">${esc(GIDER_KATEGORI[g.category]?.label ?? g.category)}</span>
                    <span class="block text-[10px] text-inksoft">${esc(g.subcategory)}</span>
                  </td>
                  <td class="py-2.5 pr-3">
                    <span class="block truncate max-w-[240px]">${esc(g.description)}</span>
                    ${g.recurring ? `<span class="text-[10px] text-inksoft">↻ her ay</span>` : ""}
                  </td>
                  <td class="py-2.5 pr-3 text-inksoft truncate max-w-[160px]">${esc(g.vendor?.name ?? "—")}</td>
                  <td class="py-2.5 pr-3 text-right whitespace-nowrap font-semibold">${money(g.amount)}</td>
                  <td class="py-2.5 whitespace-nowrap">
                    ${g.status === "odendi"
                      ? `<span class="text-[10px] font-bold rounded-full bg-teal-soft text-teal px-2 py-0.5">Ödendi</span>`
                      : g.status === "onay_bekliyor"
                        ? `<span class="text-[10px] font-bold rounded-full bg-sun-soft text-[#B07E14] px-2 py-0.5">Onay bekliyor</span>`
                        : `<span class="text-[10px] font-bold rounded-full bg-sky-soft text-sky px-2 py-0.5">Planlandı</span>`}
                  </td>
                </tr>`).join("")}
            </tbody>
          </table>
        </div>` : emptyNote("Bu dönemde gider kaydı yok.")}
    </div>`)}`;
}

// ---------- Kasa ----------
function kasaSekmesi() {
  const t = B();
  const kasa = kasaDefteri(t.id);
  if (!kasa) return card(`<div class="p-5">${sectionTitle("Kasa Defteri")}${emptyNote("Bu dönem için kasa kaydı yok.")}</div>`);

  const sorumlu = staffOf(kasa.responsibleStaffId);

  return `
  <div class="grid gap-3 sm:gap-4 grid-cols-2 xl:grid-cols-4 mb-4">
    ${[
      { label: "Devir", value: money(kasa.openingBalance), sub: "dönem başı", color: "sky" },
      { label: "Giriş", value: money(kasa.giris), sub: "takviye", color: "teal" },
      { label: "Çıkış", value: money(kasa.cikis), sub: "harcama", color: "coral" },
      { label: "Bakiye", value: money(kasa.bakiye), sub: sorumlu ? `sorumlu: ${sorumlu.name}` : "", color: "grape" },
    ].map(x => card(`
      <div class="p-4">
        <div class="text-xs font-semibold text-inksoft">${x.label}</div>
        <div class="font-display font-extrabold text-2xl mt-1 text-${x.color} break-words">${x.value}</div>
        <div class="text-[11px] text-inksoft/80 mt-0.5">${esc(x.sub)}</div>
      </div>`, "min-w-0")).join("")}
  </div>

  ${card(`
    <div class="p-5">
      ${sectionTitle("Kasa Hareketleri", `<span class="text-[11px] text-inksoft">${donemAdi(DONEM)}</span>`)}
      <p class="text-[11px] text-inksoft mb-3">
        Küçük nakit harcamalar burada izlenir — market, kırtasiye, tamir.
        Takibi olmadığında ay sonu sayım farkı açıklanamaz.
      </p>
      <ul class="space-y-1.5">
        ${kasa.movements.map(m => `
          <li class="flex items-center gap-3 rounded-xl border border-line px-3 py-2">
            <span class="w-7 h-7 rounded-full grid place-items-center shrink-0 ${m.direction === "giris" ? "bg-teal-soft text-teal" : "bg-coral-soft text-coral"}">
              ${m.direction === "giris" ? "↓" : "↑"}
            </span>
            <span class="min-w-0 flex-1">
              <span class="block text-sm truncate">${esc(m.description)}</span>
              <span class="block text-[11px] text-inksoft">${fmtDate(m.at)}${m.expenseId ? ` · gider ${esc(m.expenseId)}` : ""}</span>
            </span>
            <span class="shrink-0 font-semibold ${m.direction === "giris" ? "text-teal" : "text-coral"}">
              ${m.direction === "giris" ? "+" : "−"}${money(m.amount)}
            </span>
          </li>`).join("")}
      </ul>
    </div>`)}`;
}

/** Personel detayında gösterilen maaş & avans kartı. */
export function personelMaliKarti(kisi, branchId) {
  const b = bordroUret(branchId).items.find(x => x.staffId === kisi.id);
  if (!b) return "";
  const avans = personelAvanslari(kisi.id, branchId);
  const bakiye = avansBakiyesi(kisi.id, branchId);

  return card(`
    <div class="p-5">
      ${sectionTitle("Maaş & Avans", `<span class="text-[11px] text-inksoft">${donemAdi(DONEM)}</span>`)}

      <div class="rounded-xl bg-paper p-3">
        <div class="space-y-0.5 text-[12px]">
          <div class="flex justify-between gap-3"><span class="text-inksoft">Brüt maaş</span><span class="shrink-0">${money(b.gross)}</span></div>
          ${b.additions.map(a => `<div class="flex justify-between gap-3 text-teal"><span>${esc(a.label)}</span><span class="shrink-0">+ ${money(a.amount)}</span></div>`).join("")}
          ${b.deductions.map(d => `<div class="flex justify-between gap-3 text-coral"><span>${esc(d.label)}${d.rate ? ` (%${Math.round(d.rate * 100)})` : ""}</span><span class="shrink-0">− ${money(d.amount)}</span></div>`).join("")}
        </div>
        <div class="flex justify-between font-display font-bold text-base mt-2 pt-2 border-t border-line">
          <span>Net</span><span>${money(b.net)}</span>
        </div>
        <div class="text-[11px] text-inksoft mt-1">İşveren maliyeti: ${money(b.isverenMaliyeti)}</div>
      </div>

      ${kisi.compensationHistory?.length ? `
        <div class="mt-3">
          <div class="text-[11px] font-bold text-inksoft uppercase mb-1">Zam Geçmişi</div>
          ${kisi.compensationHistory.map(h => `
            <div class="flex items-center justify-between gap-2 text-[11px] py-0.5">
              <span class="text-inksoft truncate">${fmtDate(h.effectiveFrom)}${h.reason ? ` · ${esc(h.reason)}` : ""}</span>
              <span class="shrink-0 font-semibold">${money(h.gross)}</span>
            </div>`).join("")}
        </div>` : ""}

      <div class="mt-3 pt-3 border-t border-line">
        <div class="flex items-center justify-between gap-2">
          <span class="text-[11px] font-bold text-inksoft uppercase">Açık Avans Bakiyesi</span>
          <span class="font-display font-bold ${bakiye > 0 ? "text-coral" : "text-inksoft"}">${money(bakiye)}</span>
        </div>
        ${avans.length ? `
          <ul class="mt-2 space-y-1">
            ${avans.map(a => `
              <li class="flex items-center justify-between gap-2 text-[11px]">
                <span class="text-inksoft truncate">${fmtDate(a.requestedAt)} · ${esc(a.reason)}</span>
                <span class="shrink-0 ${a.settledAt ? "text-teal" : "text-coral font-semibold"}">
                  ${money(a.amount)}${a.settledAt ? " ✓" : ""}
                </span>
              </li>`).join("")}
          </ul>` : `<div class="text-[11px] text-inksoft mt-1">Avans kaydı yok.</div>`}
      </div>
    </div>`, "min-w-0");
}
